import React, { Fragment, useRef, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import styled from "styled-components";
import * as Time from "@sellout/utils/.dist/time";
import { Colors, Icons, Loader, LoaderSizes } from "@sellout/ui";
import { setToken } from "../utils/Auth";
import ScrollTable, {
  ScrollTableBody,
  ScrollTableBodyCell,
  ScrollTableBodyRow,
  ScrollTableHeader,
  ScrollTableHeaderCell,
  ScrollTableNoContent,
  ScrollTableSpace,
} from "./ScrollableTable";
import SET_USER_ORG_CONTEXT_ID from "@sellout/models/.dist/graphql/mutations/setUserOrgContextId.mutation";
import { useMutation } from "@apollo/react-hooks";
import TextButton, {
  TextButtonSizes,
} from "@sellout/ui/build/components/TextButton";
import { useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import * as AppActions from "../redux/actions/app.actions";
import { ModalTypes } from "./modal/Modal";
import { media } from "@sellout/ui/build/utils/MediaQuery";

const Container = styled.div`
  display: flex;
  height: fit-content;
  max-height: 100%;
  width: 100%;
  padding-bottom: 30px;
  .tableRow{
    position: relative;
  }
  .infinite-scroll-component__outerdiv {
    width: 100%;
  }

  ${media.mobile`
      padding-bottom: 10px;
    `};
  
`;

const NoContentHead = styled.div`
  font-weight: 600;
  font-size: 1.8rem;
  color: ${Colors.Grey1};
  margin-bottom: 5px;
`;

const NoContentBody = styled.div`
  font-weight: 500;
  font-size: 1.8rem;
  color: ${Colors.Grey3};
`;

const LoaderContainer = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${Colors.White};
  border-radius: 0px 0px 10px 10px;
  padding: 10px 0px;
`;

enum NoContentMessages {
  NoCustomers = "No Customers yet",
  NoOrganizations = "No organizations yet",
  Problem = "This is a problem...",
}

enum TableHeaderLabels {
  OrgId = "Org Id",
  Name = "Name",
  Email = "Email",
  OwnerEmail = "Owner Email",
  PhoneNumber = "Phone Number",
  Created = "Created",
  Organizations = "Organizations",
  Actions = "Actions",
}

enum OrganizationMessages {
  NoOrganizations = "No Organizations",
  UnknownName = "Unknown Name",
  UnknownUser = "Unknown User",
}

enum TextButtonLabels {
  Login = "Login",
  ViewSettings = "View settings",
  Edit = "Edit",
  Delete = "Delete",
}

enum MarginValues {
  SmallRight = "0px 5px 0px 0px",
}

const CommonFlexProps = {
  flex: "1",
};

type TableHeaderProps = {
  isCustomer?: boolean;
};

const TableHeader: React.FC<TableHeaderProps> = ({ isCustomer }) => (
  /** Render */
  <ScrollTableHeader className="tableRow">
    <ScrollTableHeaderCell {...CommonFlexProps} className="tableRowCell">
      {!isCustomer ? TableHeaderLabels.OrgId : TableHeaderLabels.Name}
    </ScrollTableHeaderCell>
    <ScrollTableHeaderCell {...CommonFlexProps} className="tableRowCell">
      {!isCustomer ? TableHeaderLabels.Name : TableHeaderLabels.Email}
    </ScrollTableHeaderCell>
    <ScrollTableHeaderCell {...CommonFlexProps} className="tableRowCell">
      {!isCustomer
        ? TableHeaderLabels.OwnerEmail
        : TableHeaderLabels.PhoneNumber}
    </ScrollTableHeaderCell>
    <ScrollTableHeaderCell {...CommonFlexProps} className="tableRowCell">
      {!isCustomer
        ? TableHeaderLabels.Created
        : TableHeaderLabels.Organizations}
    </ScrollTableHeaderCell>
    <ScrollTableHeaderCell {...CommonFlexProps} className="tableRowCell">
      {TableHeaderLabels.Actions}
    </ScrollTableHeaderCell>
  </ScrollTableHeader>
);

type TableRowsProps = {
  organizations: any;
  selloutWebFlowSiteId?: string;
  isCustomer?: boolean;
  refetch?: Function;
  setCurrentPage?: Function;
  scrollContainer?: any;
  setHasMore?: Function;
};

const TableRows: React.FC<TableRowsProps> = ({
  organizations,
  isCustomer,
  refetch,
  setCurrentPage,
  scrollContainer,
  setHasMore
}) => {
  /** Hooks */
  const dispatch = useDispatch();
  const setOrgId = (orgId: string) => dispatch(AppActions.setOrgId(orgId));
  const history = useHistory();
  // TODO :Refactor to use the new webflow-site-creation
  // const [addSelloutSite] = useMutation(CREATE_WEBFLOW_SITE, {
  //   refetchQueries: [{ query: QUERY_ORGANIZATIONS }],
  // });
  const [setUserOrgContextId] = useMutation(SET_USER_ORG_CONTEXT_ID, {
    onCompleted(data) {
      const { token } = data.setUserOrgContextId;
      setToken(token);
      window.location.href = "/admin/dashboard";
    },
  });
  const handleCustomerUpdate = (customerDetails) => {
    const modalPropsCustomer = {
      email: customerDetails?.email,
      phoneNumber: customerDetails?.phoneNumber,
      userId: customerDetails?.userId,
    };
    dispatch(
      AppActions.pushModal(ModalTypes.UpdateSuperAdminCustomer, {
        modalPropsCustomer,
        refetch,

      })
    );
  };
  const deleteOrganization = (organization) => {
    dispatch(
      AppActions.pushModal(ModalTypes.DeleteSuperAdminOrg, {
        organization,
        refetch,
        setCurrentPage,
        scrollContainer,
        setHasMore
      })
    );
  };
  /** Render */
  return (
    <>
      {organizations?.map((organization: any, index: number) => {
        let { _id, orgName, createdAt, isOrgDeletable } = organization;

        const email =
          organization?.user && organization?.user?.email
            ? organization.user.email
            : OrganizationMessages.UnknownUser;
        const {
          email: customerEmail,
          phoneNumber: customerPhoneNumber,
          firstName,
          lastName,
          organization: customerOrganization,
        } = organization;

        const organizationName =
          customerOrganization?.map((org: any) => org.orgName).join(", ") ||
          OrganizationMessages.NoOrganizations;
        orgName = orgName || OrganizationMessages.UnknownName;

        const fullName = `${firstName} ${lastName}`;
        return (
          <ScrollTableBodyRow key={index} height="40px">
            <ScrollTableBodyCell {...CommonFlexProps}>
              {!isCustomer ? _id : fullName}
            </ScrollTableBodyCell>
            <ScrollTableSpace />
            <ScrollTableBodyCell {...CommonFlexProps}>
              {!isCustomer ? orgName : customerEmail}
            </ScrollTableBodyCell>
            <ScrollTableSpace />
            <ScrollTableBodyCell {...CommonFlexProps}>
              {!isCustomer ? email : customerPhoneNumber}
            </ScrollTableBodyCell>
            <ScrollTableSpace />
            <ScrollTableBodyCell {...CommonFlexProps} style={true}>
              {!isCustomer ? Time.format(createdAt) : organizationName}
            </ScrollTableBodyCell>
            <ScrollTableSpace />
            <ScrollTableBodyCell {...CommonFlexProps}>
              {!isCustomer ? (
                <>
                  <TextButton
                    size={TextButtonSizes.Small}
                    children={TextButtonLabels.Login}
                    margin={MarginValues.SmallRight}
                    onClick={() => {
                      setUserOrgContextId({
                        variables: {
                          orgId: _id,
                        },
                      });
                    }}
                  />
                  <TextButton
                    size={TextButtonSizes.Small}
                    children={TextButtonLabels.ViewSettings}
                    margin={MarginValues.SmallRight}
                    onClick={() => {
                      setOrgId(_id);
                      history.push(
                        `/admin/dashboard/super/organizations/settings?orgId=${_id}`
                      );
                    }}
                  />
                  {isOrgDeletable ? (
                    <TextButton
                      size={TextButtonSizes.Small}
                      children={TextButtonLabels.Delete}
                      margin={MarginValues.SmallRight}
                      onClick={() => deleteOrganization(organization)}
                    />
                  ) : null}
                </>
              ) : (
                <TextButton
                  size={TextButtonSizes.Small}
                  children={TextButtonLabels.Edit}
                  icon={Icons.Edit}
                  margin={MarginValues.SmallRight}
                  onClick={() => handleCustomerUpdate(organization)}
                />
              )}
            </ScrollTableBodyCell>
            <ScrollTableSpace />
          </ScrollTableBodyRow>
        );
      })}
    </>
  );
};

type SuperAdminOrganizationsTableProps = {
  organizations: any;
  selloutWebFlowSiteId?: string;
  fetchMore?: Function;
  isCustomer?: boolean;
  refetch?: Function;
};

const SuperAdminOrganizationsTable: React.FC<
  SuperAdminOrganizationsTableProps
> = ({
  organizations,
  selloutWebFlowSiteId,
  fetchMore,
  isCustomer,
  refetch,
}) => {
  const scrollContainer = useRef<any>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const isCustomerScreen = organizations?.length <= 0 && isCustomer;

  const noContentHead = isCustomerScreen
    ? NoContentMessages.NoCustomers
    : NoContentMessages.NoOrganizations;

  const noContentBody = NoContentMessages.Problem;

  if (organizations?.length <= 0 || isCustomerScreen) {
    return (
      <Container style={{ width: "100%", flexDirection: "column" }}>
        <div className="tableRowGroup">
          <TableHeader isCustomer={isCustomer} />
        </div>
        <ScrollTableBody>
          <ScrollTableNoContent>
            <NoContentHead>{noContentHead}</NoContentHead>
            <NoContentBody>{noContentBody}</NoContentBody>
          </ScrollTableNoContent>
        </ScrollTableBody>
      </Container>
    );
  }

  const loadMore = async () => {
    const nextPage = currentPage + 1;
    if (!fetchMore) return;
    const { data: fetchMoreResult } = await fetchMore({
      variables: {
        pagination: {
          pageSize: 60,
          pageNumber: nextPage,
        },
        query: {
          orgQuery: "", 
          orgIds: [""],
          any: true,
        },
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;

        if (isCustomer) {
          return {
            ...prev,
            allUserProfilesAdmin: [
              ...(prev.allUserProfilesAdmin || []),
              ...(fetchMoreResult.allUserProfilesAdmin || []),
            ],
          };
        } else {
          return {
            ...prev,
            organizations: [
              ...(prev.organizations || []),
              ...(fetchMoreResult.organizations || []),
            ],
          };
        }
      },
    });

    const newItems = isCustomer
      ? fetchMoreResult?.allUserProfilesAdmin || []
      : fetchMoreResult?.organizations || [];

    if (newItems.length < 60) {
      setHasMore(false); //TODO Stop if fewer than pageSize items
    }

    setCurrentPage(nextPage); //TODO Update page
  };

  return (
    <Container style={{ width: "100%"}}>       
      <InfiniteScroll 
        dataLength={organizations?.length || 0}
        next={loadMore}
        hasMore={hasMore}
        loader={
          <LoaderContainer>
            <Loader size={LoaderSizes.Large} color={Colors.Orange} />
          </LoaderContainer>
        }
        scrollableTarget="scroll-container"
        style={{ overflow: "visible", width: "100%", display: "table" }}
      >
        <div className="tableRowGroup">
          <TableHeader isCustomer={isCustomer} />
        </div>
        <ScrollTableBody
          ref={scrollContainer}
          id="scroll-container"
          className="tableRowGroup"
          style={{ maxHeight: "70vh", overflowY: "auto" }}
        >
          <TableRows
            organizations={organizations}
            setCurrentPage={setCurrentPage}
            scrollContainer={scrollContainer}
            setHasMore={setHasMore}
            selloutWebFlowSiteId={selloutWebFlowSiteId}
            isCustomer={isCustomer}
            refetch={refetch}
          />
        </ScrollTableBody>
      </InfiniteScroll>
    </Container>
  );
};

export default SuperAdminOrganizationsTable;
